import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Star, 
  DollarSign, 
  Clock,
  Search,
  Filter,
  Download,
  Edit,
  Eye,
  Plus,
  BarChart3,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { TranscriptViewer } from '@/components/recording/TranscriptViewer';
import { SessionNotesTab } from './SessionNotesTab';

interface Transcript {
  id: string;
  booking_id: string;
  client_id: string;
  transcript_text: string;
  ai_analysis: any;
  status: string;
  created_at: string;
  recording_duration_seconds: number;
  booking_start_time: string;
  service_name: string;
  client_first_name: string;
  client_last_name: string;
}

interface MonthlyStats {
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  completionRate: number;
  averageRating: number;
  totalRevenue: number;
  newClients: number;
  repeatClients: number;
  totalReviews: number;
}

interface ReportsManagerProps {
  userId: string;
}

export const ReportsManager: React.FC<ReportsManagerProps> = ({ userId }) => {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedTranscript, setSelectedTranscript] = useState<string | null>(null);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editingTranscriptId, setEditingTranscriptId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportTypeDialogOpen, setReportTypeDialogOpen] = useState(false);
  const [selectedTranscriptForReport, setSelectedTranscriptForReport] = useState<Transcript | null>(null);

  // Fetch transcripts and medical records
  const fetchTranscripts = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_practitioner_transcripts_with_client' as any);

      if (error) throw error;

      if (data) {
        setTranscripts(data as unknown as Transcript[]);
      }
    } catch (error) {
      console.error('Error fetching transcripts:', error);
      toast({
        title: "Error loading transcripts",
        description: "Failed to load session records",
        variant: "destructive",
      });
    }
  };

  // Fetch monthly statistics
  const fetchMonthlyStats = async () => {
    try {
      const monthStart = startOfMonth(selectedMonth).toISOString();
      const monthEnd = endOfMonth(selectedMonth).toISOString();

      // Get appointments for the selected month
      const { data: appointments, error: appointmentsError } = await supabase
        .from('bookings')
        .select(`
          id,
          status,
          start_time,
          price_at_booking,
          client_id,
          currency_at_booking
        `)
        .eq('practitioner_id', userId)
        .gte('start_time', monthStart)
        .lte('start_time', monthEnd);

      if (appointmentsError) throw appointmentsError;

      // Get reviews for the selected month
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('rating, created_at')
        .eq('practitioner_id', userId)
        .gte('created_at', monthStart)
        .lte('created_at', monthEnd);

      if (reviewsError) throw reviewsError;

      // Calculate statistics
      const totalAppointments = appointments?.length || 0;
      const completedAppointments = appointments?.filter(apt => apt.status === 'Completed').length || 0;
      const cancelledAppointments = appointments?.filter(apt => apt.status === 'Cancelled').length || 0;
      const completionRate = totalAppointments > 0 ? (completedAppointments / totalAppointments) * 100 : 0;
      const averageRating = reviews?.length > 0 ? 
        reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length : 0;
      const totalRevenue = appointments?.reduce((sum, apt) => sum + (apt.price_at_booking || 0), 0) || 0;
      
      // Get unique clients for new vs repeat client analysis
      const uniqueClients = new Set(appointments?.map(apt => apt.client_id));
      const newClients = uniqueClients.size;
      const repeatClients = totalAppointments - newClients;

      setMonthlyStats({
        totalAppointments,
        completedAppointments,
        cancelledAppointments,
        completionRate,
        averageRating,
        totalRevenue,
        newClients,
        repeatClients,
        totalReviews: reviews?.length || 0
      });
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
      toast({
        title: "Error loading statistics",
        description: "Failed to load monthly statistics",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchTranscripts(), fetchMonthlyStats()]);
      setIsLoading(false);
    };

    fetchData();
  }, [userId, selectedMonth]);

  // Update transcript
  const updateTranscript = async (transcriptId: string, newText: string) => {
    try {
      const { error } = await supabase
        .from('session_transcripts')
        .update({ transcript_text: newText })
        .eq('id', transcriptId);

      if (error) throw error;

      toast({
        title: "Transcript updated",
        description: "Session record has been updated successfully",
      });

      // Refresh transcripts
      await fetchTranscripts();
      setIsEditingTranscript(false);
      setEditingTranscriptId(null);
    } catch (error) {
      console.error('Error updating transcript:', error);
      toast({
        title: "Error updating transcript",
        description: "Failed to update session record",
        variant: "destructive",
      });
    }
  };

  // Export transcript
  const exportTranscript = (transcript: Transcript) => {
    const content = `
Session Record Export
=====================

Patient: ${transcript.client_first_name} ${transcript.client_last_name}
Service: ${transcript.service_name}
Date: ${format(new Date(transcript.booking_start_time), 'MMMM d, yyyy')}
Duration: ${Math.floor(transcript.recording_duration_seconds / 60)} minutes

Transcript:
${transcript.transcript_text}

${transcript.ai_analysis ? `
AI Analysis:
${JSON.stringify(transcript.ai_analysis, null, 2)}
` : ''}

Generated on: ${format(new Date(), 'MMMM d, yyyy HH:mm')}
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-record-${transcript.client_first_name}-${transcript.client_last_name}-${format(new Date(transcript.booking_start_time), 'yyyy-MM-dd')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter transcripts based on search with proper null checks
  const filteredTranscripts = transcripts.filter(transcript => {
    const searchLower = searchTerm.toLowerCase();

    // Add null safety checks for client data
    const clientFirstName = transcript.client_first_name || '';
    const clientLastName = transcript.client_last_name || '';
    const serviceName = transcript.service_name || '';

    return (
      clientFirstName.toLowerCase().includes(searchLower) ||
      clientLastName.toLowerCase().includes(searchLower) ||
      serviceName.toLowerCase().includes(searchLower) ||
      transcript.transcript_text.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold">Reports & Analytics</h2>
        <div className="flex items-center gap-2">
          <Select value={format(selectedMonth, 'yyyy-MM')} onValueChange={(value) => setSelectedMonth(new Date(value))}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => {
                const month = subMonths(new Date(), i);
                return (
                  <SelectItem key={i} value={format(month, 'yyyy-MM')}>
                    {format(month, 'MMMM yyyy')}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 grid-rows-2 sm:grid-rows-1 h-auto gap-1 p-1">
          <TabsTrigger value="overview" className="text-xs sm:text-sm px-2 py-2 col-span-1">{t('dashboard:reportsManager.overview')}</TabsTrigger>
          <TabsTrigger value="medical-records" className="text-xs sm:text-sm px-1 sm:px-2 py-2 col-span-1">{t('dashboard:reportsManager.recordings')}</TabsTrigger>
          <TabsTrigger value="notes" className="text-xs sm:text-sm px-2 py-2 col-span-2 sm:col-span-1">{t('dashboard:reportsManager.medicalNotes')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {monthlyStats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="flex flex-col">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Appointments</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-2xl font-bold">{monthlyStats.totalAppointments}</div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                    {monthlyStats.completedAppointments} completed
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-2xl font-bold">{monthlyStats.completionRate.toFixed(1)}%</div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <XCircle className="h-3 w-3 mr-1 text-red-500" />
                    {monthlyStats.cancelledAppointments} cancelled
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
                  <Star className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-2xl font-bold">{monthlyStats.averageRating.toFixed(1)}</div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Star className="h-3 w-3 mr-1 text-yellow-500" />
                    {monthlyStats.totalReviews} reviews
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="flex-1">
                  <div className="text-2xl font-bold">${monthlyStats.totalRevenue.toFixed(2)}</div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Users className="h-3 w-3 mr-1 text-blue-500" />
                    {monthlyStats.newClients} new clients
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="medical-records" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search session records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {filteredTranscripts.map((transcript) => (
              <Card key={transcript.id}>
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base sm:text-lg truncate">
                        {transcript.client_first_name || 'Unknown'} {transcript.client_last_name || 'Client'}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {transcript.service_name || 'Unknown Service'} • {transcript.booking_start_time ? format(new Date(transcript.booking_start_time), 'MMM d, yyyy') : 'Unknown Date'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant={transcript.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                        {transcript.status}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh]">
                            <DialogHeader>
                              <DialogTitle>Session Record</DialogTitle>
                            </DialogHeader>
                            <div className="overflow-y-auto">
                              <TranscriptViewer
                                bookingId={transcript.booking_id}
                                onClose={() => {}}
                              />
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            setEditingTranscriptId(transcript.id);
                            setEditingText(transcript.transcript_text);
                            setIsEditingTranscript(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => exportTranscript(transcript)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-2 gap-1 text-xs"
                          onClick={() => {
                            setSelectedTranscriptForReport(transcript);
                            setReportTypeDialogOpen(true);
                          }}
                          title="Generate AI Report"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Report</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      Duration: {Math.floor(transcript.recording_duration_seconds / 60)} minutes
                    </div>
                    <p className="text-sm line-clamp-3">{transcript.transcript_text}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="space-y-6">
          <SessionNotesTab userId={userId} />
        </TabsContent>
      </Tabs>

      {/* Edit Transcript Modal */}
      <Dialog open={isEditingTranscript} onOpenChange={setIsEditingTranscript}>
        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Edit Session Record</DialogTitle>
                            </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="transcript-text">Transcript Text</Label>
              <Textarea
                id="transcript-text"
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                rows={10}
                className="mt-2"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditingTranscript(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                if (editingTranscriptId) {
                  updateTranscript(editingTranscriptId, editingText);
                }
              }}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Report Type Selection Dialog */}
      <Dialog open={reportTypeDialogOpen} onOpenChange={setReportTypeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Generate AI Report
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Select a report format. AI will transform the transcript into a structured report and save it to Session Notes.
          </p>
          <div className="grid gap-2 mt-2">
            {[
              { id: 'soap', label: 'SOAP Note', desc: 'Clinical standard — for insurance/billing' },
              { id: 'dap', label: 'DAP Note', desc: 'Counseling standard — simplified clinical' },
              { id: 'birp', label: 'BIRP Note', desc: 'Behavioral health — intervention focused' },
              { id: 'session-summary', label: 'Session Summary', desc: 'Client-friendly — for wellness sessions' },
              { id: 'progress', label: 'Progress Note', desc: 'Track changes across sessions' },
              { id: 'superbill', label: '📋 Superbill', desc: 'Insurance reimbursement document with ICD/CPT codes' },
            ].map((rt) => (
              <Button
                key={rt.id}
                variant="outline"
                className="justify-start h-auto py-3 px-4"
                disabled={isGeneratingReport}
                onClick={async () => {
                  if (!selectedTranscriptForReport) return;

                  // Handle superbill separately — open session notes with codes
                  if (rt.id === 'superbill') {
                    setReportTypeDialogOpen(false);
                    toast({
                      title: 'Open Session Notes',
                      description: 'Navigate to the session to add ICD/CPT codes and generate a superbill.',
                    });
                    return;
                  }

                  setIsGeneratingReport(true);
                  try {
                    const { data, error } = await supabase.functions.invoke('process-session-transcript', {
                      body: {
                        transcriptId: selectedTranscriptForReport.id,
                        transcriptText: selectedTranscriptForReport.transcript_text,
                        reportType: rt.id,
                        clientName: `${selectedTranscriptForReport.client_first_name || ''} ${selectedTranscriptForReport.client_last_name || ''}`.trim(),
                        serviceName: selectedTranscriptForReport.service_name || '',
                        sessionDate: selectedTranscriptForReport.booking_start_time,
                      },
                    });
                    if (error) throw error;

                    // Save generated report to medical_notes — format JSON to readable text
                    let reportContent = 'Report generation failed';
                    if (data?.analysis) {
                      if (typeof data.analysis === 'string') {
                        reportContent = data.analysis;
                      } else if (typeof data.analysis === 'object') {
                        // Convert JSON sections into readable markdown-style text
                        reportContent = Object.entries(data.analysis)
                          .map(([key, value]) => {
                            const heading = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                            const body = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                            return `## ${heading}\n\n${body}`;
                          })
                          .join('\n\n---\n\n');
                      }
                    }

                    const { error: noteError } = await supabase.from('medical_notes').insert({
                      title: `${rt.label} — ${selectedTranscriptForReport.client_first_name || 'Client'} ${selectedTranscriptForReport.client_last_name || ''} — ${selectedTranscriptForReport.booking_start_time ? format(new Date(selectedTranscriptForReport.booking_start_time), 'MMM d, yyyy') : ''}`,
                      content: reportContent,
                      note_type: rt.id,
                      practitioner_id: userId,
                      client_id: selectedTranscriptForReport.client_id,
                      booking_id: selectedTranscriptForReport.booking_id,
                      is_private: false,
                      tags: ['ai-generated', rt.id],
                    });
                    if (noteError) throw noteError;

                    toast({
                      title: 'Report generated',
                      description: `${rt.label} has been saved to Session Notes.`,
                    });
                    setReportTypeDialogOpen(false);
                  } catch (err) {
                    console.error('Generate report error:', err);
                    toast({
                      title: 'Report generation failed',
                      description: 'Please try again later.',
                      variant: 'destructive',
                    });
                  } finally {
                    setIsGeneratingReport(false);
                  }
                }}
              >
                <div className="text-left">
                  <div className="font-medium flex items-center gap-2">
                    {isGeneratingReport && <Loader2 className="h-3 w-3 animate-spin" />}
                    {rt.label}
                  </div>
                  <div className="text-xs text-muted-foreground">{rt.desc}</div>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
